import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { AppImage, useStacklineTheme } from '@stackline/ui';
import { ProductGrowthFilterModalHeader } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/ProductGrowthFilterModal';
import { AppFilterModalFooter } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterModal';
import { useMicroTrendFilterModalStore, useMicroTrendsFilterStore } from './MicroTrendStore';
import { useMainEntityId } from 'src/utils/Hooks/reduxSelectors';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';
import { FormControlLabel, TextField } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import colors from 'src/utils/colors';
import { FilterKey, generateFilterUrlString } from 'src/components/BeaconV2Components/common/AppFilterButton/utils';

const GenericStyledDialogue = styled(Dialog)({
  '& .MuiPaper-root': {
    width: '752px',
    height: '641px',
    boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.06)',
    border: 'solid 1px #dedede',
    backgroundColor: '#fff',
    borderRadius: '8px'
  }
});

// Reuse AppFilterModal search input styling locally (no stylesheet changes)
const FilterSearchInput = ({
  input,
  handleChange,
  placeholderText = ''
}: {
  input: string;
  handleChange: (s: string) => void;
  placeholderText?: string;
}) => {
  const theme = useStacklineTheme();
  return (
    <TextField
      variant="standard"
      value={input}
      onChange={(event) => handleChange(event.target.value)}
      InputProps={{
        placeholder: placeholderText,
        disableUnderline: true,
        startAdornment: (
          <AppImage
            src="https://media.brandclub.com/brandclub/image_asset/icon_search.svg"
            alt="search-icon"
            style={{ marginRight: 10 }}
          />
        ),
        autoComplete: 'off',
        id: 'type-search'
      }}
      sx={{
        borderRadius: 17,
        border: `1px solid ${theme.colors.primaryGray}`,
        height: 30,
        width: 290,
        backgroundColor: '#fff',
        '& div.MuiInput-root': {
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          height: 30,
          '& input': {
            padding: 0,
            fontSize: '12px',
            fontFamily: 'Roboto',
            color: theme.colors.primary,
            '::placeholder': { opacity: 1, color: colors.blueGrey }
          }
        }
      }}
    />
  );
};

const SubcategoryGrid = ({
  options,
  onToggle,
  placeholderText
}: {
  options: { id: number; label: string; isSelected: boolean }[];
  onToggle: (id: number) => void;
  placeholderText: string;
}) => {
  const [input, setInput] = useState('');
  const theme = useStacklineTheme();

  const filtered = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [input, options]);

  return (
    <Flex flexDirection="column">
      <Flex marginTop={theme.spacing.md} marginBottom="10px">
        <FilterSearchInput input={input} handleChange={setInput} placeholderText={placeholderText} />
      </Flex>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, auto)',
          color: theme.colors.primary,
          justifyContent: 'space-between',
          rowGap: '16px',
          marginTop: '10px',
          marginBottom: theme.spacing.sm
        }}
      >
        {filtered.map((opt) => (
          <FormControlLabel
            key={opt.id}
            title={opt.label}
            sx={{
              gap: '8px',
              color: theme.colors.primary,
              minWidth: '190px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginLeft: '0px',
              marginRight: '0px',
              '& .MuiTypography-root': { width: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }
            }}
            checked={opt.isSelected}
            label={opt.label}
            value={opt.id}
            control={<StyledCheckbox iconProps={{ width: '14px', height: '14px' }} />}
            onChange={() => onToggle(opt.id)}
          />
        ))}
        {filtered.length < 3 &&
          Array.from({ length: 3 - filtered.length }).map((_, index) => (
            <div key={`placeholder-${index}`} style={{ width: '192px', display: 'hidden' }}></div>
          ))}
      </div>
    </Flex>
  );
};

const MicroTrendFilterModal: React.FC = () => {
  const { isOpen, closeModal } = useMicroTrendFilterModalStore();
  const { selectedCategoryIds, selectedSubcategoryIds, setSelectedCategoryIds, setSelectedSubcategoryIds, clear } =
    useMicroTrendsFilterStore();
  const categoryId = useMainEntityId();
  const { getEntityDisplayName } = useEntityMetadataEnhancer();
  const theme = useStacklineTheme();
  const [localCategorySelection, setLocalCategorySelection] = useState<number[]>(selectedCategoryIds);
  const [localSubcategorySelection, setLocalSubcategorySelection] = useState<number[]>(selectedSubcategoryIds);
  const history = useHistory();

  // Get categories, subcategories and filters from Redux state
  const categories = useAppSelector((state) => state.categories);
  const subcategories = useAppSelector((state) => state.subCategories);
  const filters = useAppSelector((state) => state.filters);
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);

  // Check if we're at "all categories" level
  const isAllCategoriesLevel =
    !categoryId || categoryId === '0' || (mainEntity?.type === 'category' && String(mainEntity.id) === '0');

  // Get all available category IDs
  const allCategoryIds = useMemo(() => {
    if (!categories) return [];
    return categories.map((cat: any) => Number(cat.id)).filter((id) => id !== 0);
  }, [categories]);

  // Get subcategories filtered by selected categories (dynamic filtering)
  const availableSubcategoryIds = useMemo(() => {
    if (!subcategories) return [];

    // If at specific category level, only show subcategories for that category
    if (!isAllCategoriesLevel && categoryId) {
      return subcategories
        .filter((sub: any) => Number(sub.parentCategoryId) === Number(categoryId))
        .map((sub: any) => Number(sub.id));
    }

    // If at all categories level and categories are selected, filter subcategories
    if (isAllCategoriesLevel && localCategorySelection.length > 0) {
      const selectedCategorySet = new Set(localCategorySelection);
      return subcategories
        .filter((sub: any) => selectedCategorySet.has(Number(sub.parentCategoryId)))
        .map((sub: any) => Number(sub.id));
    }

    // If at all categories level and no categories selected, show all subcategories
    return subcategories.map((sub: any) => Number(sub.id));
  }, [subcategories, categoryId, isAllCategoriesLevel, localCategorySelection]);
  // Fixed initial order (selected first, then unitsSold desc)
  const [orderedSubcategoryIds, setOrderedSubcategoryIds] = useState<number[]>([]);

  // When the modal opens, initialize local state from store or URL filters (only once on open)
  useEffect(() => {
    if (!isOpen) return;

    // Sync categories
    if (selectedCategoryIds.length > 0) {
      setLocalCategorySelection(selectedCategoryIds);
    } else {
      const reduxCategoryIds = (filters?.category || []).map((c: any) => Number(c.id)).filter((id: number) => !!id);
      setLocalCategorySelection(reduxCategoryIds);
    }

    // Sync subcategories
    if (selectedSubcategoryIds.length > 0) {
      setLocalSubcategorySelection(selectedSubcategoryIds);
    } else {
      const reduxSubcategoryIds = (filters?.subcategory || [])
        .map((s: any) => Number(s.id))
        .filter((id: number) => !!id);
      const available = new Set(availableSubcategoryIds);
      const prefill = reduxSubcategoryIds.filter((id: number) => available.has(id));
      setLocalSubcategorySelection(prefill);
    }
    // Only run when modal opens, not when local selections change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Establish a fixed order when modal opens or when available subcategories change or when selection changes
  useEffect(() => {
    if (!isOpen || !subcategories) return;

    const subcategoryById = new Map(
      (subcategories || []).map((s: any, i: number) => [Number(s.id), { ...s, __index: i }])
    );

    const items = availableSubcategoryIds.map((id, idx) => {
      const sub: any = subcategoryById.get(Number(id));
      const retailSales = Number(sub?.retailSales || 0);
      const originalIndex = (sub as any)?.__index ?? idx;
      const isSelected = localSubcategorySelection.includes(id);
      return { id, retailSales, originalIndex, isSelected };
    });

    const selectedItems = items
      .filter((x) => x.isSelected)
      .sort((a, b) => {
        if (b.retailSales !== a.retailSales) return b.retailSales - a.retailSales;
        return a.originalIndex - b.originalIndex;
      });
    const unselectedItems = items
      .filter((x) => !x.isSelected)
      .sort((a, b) => {
        if (b.retailSales !== a.retailSales) return b.retailSales - a.retailSales;
        return a.originalIndex - b.originalIndex;
      });

    setOrderedSubcategoryIds([...selectedItems, ...unselectedItems].map((x) => x.id));
    // Recalculate order when modal opens, when category selection changes, or when subcategory selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, availableSubcategoryIds, localSubcategorySelection]);

  // Global sync: when AppFilterModal (Redux/URL) changes while modal is CLOSED, update micro store
  // This keeps the micro store in sync with the global app filter
  useEffect(() => {
    // Only sync when modal is closed to avoid interfering with user interaction
    if (isOpen) return;

    // Sync categories from Redux filters
    const reduxCategoryIds = (filters?.category || []).map((c: any) => Number(c.id)).filter((id: number) => !!id);
    const catA = new Set(selectedCategoryIds);
    const catB = new Set(reduxCategoryIds);
    if (!(catA.size === catB.size && Array.from(catA).every((id) => catB.has(id)))) {
      setSelectedCategoryIds(reduxCategoryIds);
    }

    // Sync subcategories from Redux filters
    const reduxSubcategoryIds = (filters?.subcategory || []).map((s: any) => Number(s.id)).filter((id: number) => !!id);
    const subA = new Set(selectedSubcategoryIds);
    const subB = new Set(reduxSubcategoryIds);
    if (!(subA.size === subB.size && Array.from(subA).every((id) => subB.has(id)))) {
      setSelectedSubcategoryIds(reduxSubcategoryIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters?.category, filters?.subcategory]);

  // Category options (for all categories level) - sorted with selected first
  const categoryOptions = useMemo(() => {
    if (!categories) return [];
    const options = allCategoryIds.map((id, idx) => {
      const cat: any = categories.find((c: any) => Number(c.id) === id);
      const label = cat?.name || getEntityDisplayName(id, 'categoryId');
      const isSelected = localCategorySelection.includes(id);
      const retailSales = Number(cat?.retailSales || 0);
      return { id, label, isSelected, retailSales, originalIndex: idx };
    });

    const selectedItems = options
      .filter((x) => x.isSelected)
      .sort((a, b) => {
        if (b.retailSales !== a.retailSales) return b.retailSales - a.retailSales;
        return a.originalIndex - b.originalIndex;
      });
    const unselectedItems = options
      .filter((x) => !x.isSelected)
      .sort((a, b) => {
        if (b.retailSales !== a.retailSales) return b.retailSales - a.retailSales;
        return a.originalIndex - b.originalIndex;
      });

    return [...selectedItems, ...unselectedItems];
  }, [categories, allCategoryIds, localCategorySelection, getEntityDisplayName]);

  // Subcategory options (dynamically filtered by selected categories)
  const subcategoryOptions = useMemo(() => {
    const subcategoryById = new Map((subcategories || []).map((s: any) => [Number(s.id), s]));
    const idsInOrder = orderedSubcategoryIds.length > 0 ? orderedSubcategoryIds : availableSubcategoryIds;
    return idsInOrder.map((id) => {
      const sub: any = subcategoryById.get(Number(id));
      const label = sub?.displayName || getEntityDisplayName(id, 'subcategoryId');
      const isSelected = localSubcategorySelection.includes(id);
      return { id, label, isSelected };
    });
  }, [orderedSubcategoryIds, availableSubcategoryIds, localSubcategorySelection, subcategories, getEntityDisplayName]);

  const handleCategoryToggle = (id: number) => {
    setLocalCategorySelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubcategoryToggle = (id: number) => {
    setLocalSubcategorySelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const pushMergedFilterToUrl = (nextSelectedCategoryIds: number[], nextSelectedSubcategoryIds: number[]) => {
    const filterParamsFromUrl = new URLSearchParams(window.location.search);
    let existingFilter: any = {};
    try {
      existingFilter = JSON.parse(filterParamsFromUrl.get('filter') || '{}');
    } catch (e) {
      existingFilter = {};
    }

    const selectedCategoryObjects = categories
      .filter((cat: any) => nextSelectedCategoryIds.includes(Number(cat.id)))
      .map((cat: any) => ({ ...cat, isSelected: true }));

    const selectedSubcategoryObjects = subcategories
      .filter((sub: any) => nextSelectedSubcategoryIds.includes(Number(sub.id)))
      .map((sub: any) => ({ ...sub, isSelected: true }));

    const mergedFilterValues = {
      ...existingFilter,
      ...(isAllCategoriesLevel &&
        nextSelectedCategoryIds.length > 0 && { [FilterKey.Category]: selectedCategoryObjects }),
      [FilterKey.Subcategory]: selectedSubcategoryObjects
    } as any;

    const { generatedFilterParams } = generateFilterUrlString(mergedFilterValues);

    filterParamsFromUrl.delete('filter');
    const base = filterParamsFromUrl.toString();
    const finalParams = `${base}${generatedFilterParams}`;
    history.push(`?${finalParams}`);
  };

  const handleApply = () => {
    setSelectedCategoryIds(localCategorySelection);
    setSelectedSubcategoryIds(localSubcategorySelection);
    pushMergedFilterToUrl(localCategorySelection, localSubcategorySelection);
    closeModal();
  };

  const handleClear = () => {
    setLocalCategorySelection([]);
    setLocalSubcategorySelection([]);
    clear();

    const filterParamsFromUrl = new URLSearchParams(window.location.search);
    let existingFilter: any = {};
    try {
      existingFilter = JSON.parse(filterParamsFromUrl.get('filter') || '{}');
    } catch (e) {
      existingFilter = {};
    }

    // Remove Category and Subcategory from existing filter but keep other keys intact
    if (existingFilter && existingFilter[FilterKey.Category]) {
      delete existingFilter[FilterKey.Category];
    }
    if (existingFilter && existingFilter[FilterKey.Subcategory]) {
      delete existingFilter[FilterKey.Subcategory];
    }

    const { generatedFilterParams } = generateFilterUrlString(existingFilter);
    filterParamsFromUrl.delete('filter');
    const base = filterParamsFromUrl.toString();
    const finalParams = `${base}${generatedFilterParams}`;
    history.push(`?${finalParams}`);
  };

  return (
    <GenericStyledDialogue open={isOpen} onClose={closeModal}>
      <ProductGrowthFilterModalHeader handleClose={closeModal} />
      <div
        className="inner-container"
        style={{ height: '500px', overflow: 'scroll', overflowX: 'hidden', paddingBottom: '35px' }}
      >
        <Flex flexDirection="column" sx={{ paddingLeft: '48px', paddingRight: '43px' }}>
          {/* Show categories filter only at "all categories" level */}
          {isAllCategoriesLevel && (
            <>
              <Flex marginTop={theme.spacing.mdl}>
                <Text variant="subtitle1">Categories</Text>
              </Flex>
              <SubcategoryGrid
                options={categoryOptions}
                onToggle={handleCategoryToggle}
                placeholderText="Search categories"
              />
            </>
          )}

          {/* Always show subcategories filter */}
          <Flex marginTop={theme.spacing.mdl}>
            <Text variant="subtitle1">Subcategories</Text>
          </Flex>
          <SubcategoryGrid
            options={subcategoryOptions}
            onToggle={handleSubcategoryToggle}
            placeholderText="Search subcategories"
          />
        </Flex>
      </div>
      <AppFilterModalFooter handleClearFilters={handleClear} handleApplyFilters={handleApply} />
    </GenericStyledDialogue>
  );
};

export default MicroTrendFilterModal;
